import { GetStaticProps } from 'next';
import { EventOverviewSingleFieldsFragment, PageInformationFieldsFragment, Theme } from 'api/graphql-types';
import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetEventOverview } from 'api/graphql-helper';
import { Head } from 'modules/head/head';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { initialState as eventsInitialState } from 'redux/stores/events';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import React from 'react';
import IntroHeader from 'modules/intro-header/intro-header';
import { ROUTES } from 'constants/routes';
import { IBasePageProps } from 'types/page';
import { searchEvents } from 'api/algolia';
import { EVENTS_OVERVIEW_LIMIT } from 'modules/events-overview/data';

interface IEventOverviewPageProps extends IBasePageProps {
	eventOverview?: EventOverviewSingleFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
}

const EventOverviewPage = (props: IEventOverviewPageProps): React.JSX.Element => {
	const modules = props.eventOverview?.modules || [];
	const seoModule = props?.eventOverview?.seo;

	return (
		<Layout navbar={props?.pageInformation?.navbar} footer={props?.pageInformation?.footer}>
			<Head seoModule={seoModule} seoParent={props.pageInformation?.seo} />
			<IntroHeader module={props?.eventOverview?.header} slug={ROUTES.EVENTS.fullPath} />
			<ModuleBuilder modules={modules} slug={ROUTES.EVENTS.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps<IEventOverviewPageProps> = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetEventOverview({ variables: { locale, publicationState } }, client);
		const { pageInformation, eventOverview, themes_connection: themes } = result.props.data;

		const { results: events, total: eventsCount } = await searchEvents(
			'',
			{
				themesIds: []
			},
			{
				sort: 'asc',
				page: 1,
				limit: EVENTS_OVERVIEW_LIMIT,
				locale
			}
		);

		if (eventOverview == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				pageInformation: pageInformation?.data?.attributes,
				eventOverview: eventOverview?.data?.attributes,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'event-overview',
						contentTypeId: eventOverview?.data?.documentId
					},
					events: {
						...eventsInitialState,
						events,
						eventsCount
					},
					themes: {
						themes: (themes?.data as Theme[]) || [],
						totalThemes: themes?.pageInfo.total || 0
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in event overview', error);

		return {
			notFound: true
		};
	}
};

export default EventOverviewPage;
